import React, { useState, useEffect } from 'react';
import { validateEmail, signupToWaitlist } from '../../utils';
import EmailPopup from '../modals/EmailPopup';
import { Badge, ChevronDownIcon, CheckIcon, ArrowRightIcon } from '../ui';
import { AppPreview, ShareCardInline, SessionSwitchLink } from '../features';
import { TYPOGRAPHY, BREAKPOINTS, TEXT_SHADOWS, ANIMATIONS } from '../../constants';

const MobileHeroLayout = ({ 
  windowSize, 
  referrals, 
  setReferrals, 
  hasJoined: initialHasJoined,
  queuePosition,
  onWaitlistSuccess,
  onReferFriend,
  referredBy,
  // Additional props for referral popup
  referralLink,
  referralCount = 0,
  // Session switching (returning user on a friend's link / different email)
  hasExistingSession = false,
  onShowExisting,
  onUseDifferentEmail
}) => {
  const [showEmailPopup, setShowEmailPopup] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [hasJoined, setHasJoined] = useState(initialHasJoined || false);
  const [showInlineCard, setShowInlineCard] = useState(false);

  // Keep local joined state in sync when the parent switches views
  useEffect(() => {
    setHasJoined(initialHasJoined || false);
    if (!initialHasJoined) setShowInlineCard(false);
  }, [initialHasJoined]);

  const handleShowReferral = () => {
    if (onReferFriend) onReferFriend();
    setShowInlineCard(true);
  };

  // Get responsive typography and spacing based on mobile breakpoints (320px-767px)
  const getResponsiveSpecs = () => {
    const width = windowSize?.width || BREAKPOINTS.MOBILE_MD; // Default to iPhone size
    
    if (width <= BREAKPOINTS.MOBILE_SM) {
      // Very small phones (320px and below) - iPhone SE
      return {
        titleSize: TYPOGRAPHY.MOBILE.TITLE_SM, // 30px - More compact for small screens
        paragraphSize: TYPOGRAPHY.MOBILE.SUBTITLE_SM, // 18px - Compact paragraph
        buttonSize: TYPOGRAPHY.MOBILE.BUTTON_SM, // 14px - Small button text
        titleLineHeight: TYPOGRAPHY.LINE_HEIGHTS.TIGHT, // 1.296
        paragraphLineHeight: TYPOGRAPHY.LINE_HEIGHTS.NORMAL, // 1.5
        titleFontWeight: TYPOGRAPHY.WEIGHTS.SEMIBOLD, // 600
        paragraphFontWeight: TYPOGRAPHY.WEIGHTS.LIGHT, // 300
        textShadow: TEXT_SHADOWS.HERO_MOBILE_STRONG, // Strong shadow for readability
        mainPadding: '0 0.75rem', // 12px horizontal padding
        titleMargin: '0.75rem', // 12px title margin
        paragraphMargin: '1rem', // 16px paragraph margin  
        appPreviewMargin: '1.5rem 0', // 24px vertical margin
        maxTitleWidth: '95%', // Use most of screen width
        maxParagraphWidth: '90%', // Slightly narrower for readability
        headerPadding: '0 0.75rem', // 12px header padding
        headerTop: '1rem', // 16px header top
        buttonContainerMaxWidth: '280px', // Smaller button for small screens
        buttonHeight: '3.5rem', // 56px - Slightly smaller
        buttonFontSize: '1.125rem', // 18px
        paddingTop: '5rem', // Account for header
        paddingBottom: '2rem',
      };
    } else if (width <= BREAKPOINTS.MOBILE_MD) {
      // Standard mobile phones (321px-375px) - iPhone 12/13/14
      return {
        titleSize: TYPOGRAPHY.MOBILE.TITLE, // 36px - Standard mobile title
        paragraphSize: TYPOGRAPHY.MOBILE.SUBTITLE, // 20px - Standard paragraph
        buttonSize: TYPOGRAPHY.MOBILE.BUTTON, // 16px - Standard button text
        titleLineHeight: TYPOGRAPHY.LINE_HEIGHTS.TIGHT,
        paragraphLineHeight: TYPOGRAPHY.LINE_HEIGHTS.NORMAL,
        titleFontWeight: TYPOGRAPHY.WEIGHTS.SEMIBOLD,
        paragraphFontWeight: TYPOGRAPHY.WEIGHTS.LIGHT,
        textShadow: TEXT_SHADOWS.HERO_MOBILE_STRONG,
        mainPadding: '0 1rem', // 16px horizontal padding
        titleMargin: '0.75rem', // 12px title margin - Reduced for iPhone 12 Pro
        paragraphMargin: '0.75rem', // 12px paragraph margin - Reduced significantly
        appPreviewMargin: '1rem 0', // 16px vertical margin - Reduced for better fit
        maxTitleWidth: '95%',
        maxParagraphWidth: '90%',
        headerPadding: '0 1rem',
        headerTop: '1.125rem', // 18px header top (from current App.jsx)
        buttonContainerMaxWidth: '320px',
        buttonHeight: '4rem', // 64px - Full size button
        buttonFontSize: '1.25rem', // 20px
        paddingTop: '5.5rem', // Account for header
        paddingBottom: '2rem',
      };
    } else if (width <= BREAKPOINTS.MOBILE_LG) {
      // Large mobile phones (376px-428px) - iPhone XR, iPhone 14 Plus
      return {
        titleSize: '2.5rem', // 40px - Slightly larger for bigger screens
        paragraphSize: '1.375rem', // 22px - Slightly larger paragraph
        buttonSize: TYPOGRAPHY.MOBILE.BUTTON, // 16px - Keep button text consistent
        titleLineHeight: TYPOGRAPHY.LINE_HEIGHTS.TIGHT,
        paragraphLineHeight: TYPOGRAPHY.LINE_HEIGHTS.NORMAL,
        titleFontWeight: TYPOGRAPHY.WEIGHTS.SEMIBOLD,
        paragraphFontWeight: TYPOGRAPHY.WEIGHTS.LIGHT,
        textShadow: TEXT_SHADOWS.HERO_MOBILE_MEDIUM, // Medium shadow for larger screens
        mainPadding: '0 1.25rem', // 20px horizontal padding
        titleMargin: '0.875rem', // 14px title margin - Reduced for iPhone XR
        paragraphMargin: '1rem', // 16px paragraph margin - Reduced significantly
        appPreviewMargin: '1.25rem 0', // 20px vertical margin - Reduced for better fit
        maxTitleWidth: '95%',
        maxParagraphWidth: '85%',
        headerPadding: '0 1.25rem',
        headerTop: '1.125rem',
        buttonContainerMaxWidth: '360px',
        buttonHeight: '4rem', // 64px
        buttonFontSize: '1.25rem', // 20px
        paddingTop: '5.5rem',
        paddingBottom: '2rem',
      };
    } else {
      // Very large phones/small tablets (429px-767px)
      return {
        titleSize: '2.75rem', // 44px - Approaching tablet sizes
        paragraphSize: '1.5rem', // 24px - Larger paragraph
        buttonSize: '1.125rem', // 18px - Slightly larger button text
        titleLineHeight: TYPOGRAPHY.LINE_HEIGHTS.TIGHT,
        paragraphLineHeight: TYPOGRAPHY.LINE_HEIGHTS.NORMAL,
        titleFontWeight: TYPOGRAPHY.WEIGHTS.SEMIBOLD,
        paragraphFontWeight: TYPOGRAPHY.WEIGHTS.LIGHT,
        textShadow: TEXT_SHADOWS.HERO_MOBILE_LIGHT, // Light shadow for larger screens
        mainPadding: '0 1.5rem', // 24px horizontal padding
        titleMargin: '1.25rem', // 20px title margin
        paragraphMargin: '1.75rem', // 28px paragraph margin
        appPreviewMargin: '2.5rem 0', // 40px vertical margin
        maxTitleWidth: '90%',
        maxParagraphWidth: '85%',
        headerPadding: '0 1.5rem',
        headerTop: '1.125rem',
        buttonContainerMaxWidth: '400px',
        buttonHeight: '4rem', // 64px
        buttonFontSize: '1.375rem', // 22px
        paddingTop: '6rem', // More space for larger screens
        paddingBottom: '2.5rem',
      };
    }
  };

  const { 
    titleSize, 
    paragraphSize, 
    buttonSize,
    titleLineHeight,
    paragraphLineHeight,
    titleFontWeight,
    paragraphFontWeight,
    textShadow,
    mainPadding,
    titleMargin,
    paragraphMargin,
    appPreviewMargin,
    maxTitleWidth,
    maxParagraphWidth,
    headerPadding,
    headerTop,
    buttonContainerMaxWidth,
    buttonHeight,
    buttonFontSize,
    paddingTop,
    paddingBottom
  } = getResponsiveSpecs();

  const handleJoinSuccess = (data) => {
    setUserEmail(data.email);
    setHasJoined(true);
    if (data.referral_code) {
      setReferrals(prev => ({ ...prev, user: data.referral_code }));
    }
    // Call the parent's success handler
    if (onWaitlistSuccess) {
      onWaitlistSuccess(data);
    }
  };

  return (
    <div
      style={{
        width: '100%',
        minHeight: '100vh',
        backgroundImage: 'url("/mobile-hero-bg.jpg")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'Inter, sans-serif',
        position: 'relative',
      }}
    >
      {/* Header */}
      <header
        style={{
          position: 'absolute',
          top: headerTop,
          left: '0',
          right: '0',
          zIndex: 20,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: headerPadding,
        }}
      >
        <Badge variant="ffsLogo" size="mobile" />
        <Badge variant="earlyAccess" size="mobile" />
      </header>

      {/* Main Content */}
      <main
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          padding: mainPadding,
          paddingTop: paddingTop,
          paddingBottom: paddingBottom,
          overflow: 'auto', // Allow scrolling if content doesn't fit
        }}
      >
        {/* Hero Title - Split into two lines per Figma design */}
        <h1
          className="mobile-hero-title"
          style={{
            fontFamily: TYPOGRAPHY.FONTS.MONTSERRAT.join(', '),
            fontWeight: 600, // Semibold
            fontSize: '2rem', // 32px - Fixed for mobile
            color: 'white',
            lineHeight: titleLineHeight,
            marginBottom: titleMargin,
            width: '100%', // Full width to prevent wrapping
            textShadow: textShadow,
            textAlign: 'center',
            margin: `0 auto ${titleMargin}`,
          }}
        >
          Train Your Mind.
          <br />
          Dominate The Pitch.
        </h1>

        {/* Hero Paragraph - Updated to match Figma design */}
        <p
          className="hero-paragraph"
          style={{
            fontFamily: TYPOGRAPHY.FONTS.POPPINS.join(', '),
            fontWeight: paragraphFontWeight,
            fontSize: paragraphSize,
            color: 'white',
            lineHeight: paragraphLineHeight,
            marginBottom: paragraphMargin,
            maxWidth: maxParagraphWidth,
            textShadow: textShadow,
            textAlign: 'center',
            margin: `0 auto ${paragraphMargin}`,
            paddingLeft: '0.75rem', // 12px
            paddingRight: '0.75rem', // 12px
          }}
        >
          The meditation app built exclusively for footballers. Master your mindset, find flow fast and perform, when it matters most.
        </p>

        {/* App Preview / Referral Component - Key visual element from Figma */}
        <div 
          style={{ 
            margin: appPreviewMargin,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%'
          }}
        >
          <AppPreview />
        </div>

        {/* Scroll Down Button - Visual indicator (only show if not joined) */}
        {!hasJoined && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: windowSize?.width <= BREAKPOINTS.MOBILE_LG ? '0.75rem' : '1.5rem', // Reduced spacing for iPhone XR & 12 Pro
              animation: `bounce 2s infinite ease-in-out`,
            }}
          >
            <div
              style={{
                width: '48px',
                height: '48px',
                backgroundColor: 'white',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <ChevronDownIcon 
                size={20} 
                color="#0D0E20" 
                strokeWidth="1"
              />
            </div>
            <style>
              {`
                @keyframes bounce {
                  0%, 20%, 50%, 80%, 100% {
                    transform: translateY(0);
                  }
                  40% {
                    transform: translateY(-8px);
                  }
                  60% {
                    transform: translateY(-4px);
                  }
                }
              `}
            </style>
          </div>
        )}

        {/* Email Input and Button - Only show if not joined */}
        {!hasJoined && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              width: '100%',
              maxWidth: buttonContainerMaxWidth,
              alignItems: 'center',
            }}
          >
            <button
              onClick={() => setShowEmailPopup(true)}
              style={{
                width: '100%',
                height: buttonHeight,
                backgroundColor: 'white',
                color: '#0D0E20',
                border: 'none',
                borderRadius: '1.25rem', // 20px
                fontSize: buttonFontSize,
                fontFamily: TYPOGRAPHY.FONTS.INTER.join(', '),
                fontWeight: TYPOGRAPHY.WEIGHTS.SEMIBOLD,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
                padding: '0 1rem', // Add horizontal padding for better text spacing
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 15px 35px rgba(0, 0, 0, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 10px 30px rgba(0, 0, 0, 0.3)';
              }}
            >
              Join Waitlist
            </button>

            {/* Returning user viewing a friend's referral link */}
            {hasExistingSession && (
              <SessionSwitchLink onClick={onShowExisting} align="center" style={{ marginTop: 0 }}>
                Already joined? View your referral link
              </SessionSwitchLink>
            )}
          </div>
        )}

        {/* Success State Buttons - Show if joined (from Figma "Joined" design) */}
        {hasJoined && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              width: '100%',
              maxWidth: buttonContainerMaxWidth,
              alignItems: 'center',
            }}
          >
          {showInlineCard && (
            <ShareCardInline
              referralLink={referralLink}
              referralCount={referralCount}
              onClose={() => setShowInlineCard(false)}
              deviceType="mobile"
            />
          )}
            {/* Joined Button - Dark with checkmark */}
            <button
              style={{
                width: '100%',
                height: buttonHeight,
                backgroundColor: '#0D0E20', // Dark background matching the design
                color: 'white',
                border: 'none',
                borderRadius: '1.25rem', // 20px
                fontSize: buttonFontSize,
                fontFamily: TYPOGRAPHY.FONTS.INTER.join(', '),
                fontWeight: TYPOGRAPHY.WEIGHTS.SEMIBOLD,
                cursor: 'default', // Not clickable
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem', // 8px gap between icon and text
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
                transition: 'all 0.2s ease',
              }}
            >
              <CheckIcon 
                size={windowSize?.width <= BREAKPOINTS.MOBILE_MD ? 18 : 20} 
                color="white" 
                strokeWidth="2.5"
              />
              Joined
            </button>

            {/* Refer a Friend Button - Black with handshake icon */}
            <button
              onClick={handleShowReferral}
              style={{
                width: '100%',
                height: buttonHeight,
                backgroundColor: '#000000', // Pure black matching desktop
                color: 'white',
                border: 'none',
                borderRadius: '1.25rem', // 20px
                fontSize: buttonFontSize,
                fontFamily: TYPOGRAPHY.FONTS.INTER.join(', '),
                fontWeight: TYPOGRAPHY.WEIGHTS.SEMIBOLD,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem', // 8px gap between elements
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#333333'; // Darker on hover
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 15px 35px rgba(0, 0, 0, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#000000';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 10px 30px rgba(0, 0, 0, 0.3)';
              }}
            >
              <span style={{ fontSize: '1.125rem' }}>🏆</span>
              <span>Refer a Friend</span>
              <svg
                width="20"
                height="20"
                viewBox="0 0 26 26"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  width: '1.25rem', // 20px for mobile
                  height: '1.25rem', // 20px for mobile
                  filter: "brightness(0) invert(1)", // White color
                }}
              >
                <rect
                  width="26"
                  height="26"
                  fill="url(#pattern0_more_mobile)"
                />
                <defs>
                  <pattern
                    id="pattern0_more_mobile"
                    patternContentUnits="objectBoundingBox"
                    width="1"
                    height="1"
                  >
                    <use
                      href="#image0_more_mobile"
                      transform="scale(0.01)"
                    />
                  </pattern>
                  <image
                    id="image0_more_mobile"
                    width="100"
                    height="100"
                    preserveAspectRatio="none"
                    href="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGQAAABkCAYAAABw4pVUAAAACXBIWXMAAAsTAAALEwEAmpwYAAABIklEQVR4nO3dMU7EMABFwT0SXIdut+IGtFycfQghCqSlzheZ6dxZebHTRPblAgAAAAAAwGGqJ49/RPVe3avXo+dyen3H+CHKkarn6qPfvsa307+pB0a5PohipRwpUfYkyp5E2ZMoexJlT6LsSZQ9ibInUfYkyp5E2ZMoexJlT6LsSZQ9ibInUfYkyp5E2ZMoexJlT6LsSZQ9ibInUfYkyp5E2VPd/vjB+3r03E6rxyvla/xy9NxOKUF2ZMvakY/6jsTYkRg7EmNHYuxIjB2JsSMxdiTGjsTYkRg7EmNHYuxIjB2JsSMxdiTGjsTYkRg7EmNHYuxIjB2JsSMxdiTGjsTYkRg7EmNHDuPfU705hH8zyt2FLkNy5REAAAAAAMDlv/kEAny3FazsZ1QAAAAASUVORK5CYII="
                  />
                </defs>
              </svg>
            </button>

            {/* Sign up another email in this browser (friend / testing) */}
            <SessionSwitchLink onClick={onUseDifferentEmail} align="center" style={{ marginTop: 0 }}>
              Not you? Use a different email
            </SessionSwitchLink>
          </div>
        )}
      </main>

      {/* Email Popup */}
      <EmailPopup
        isOpen={showEmailPopup}
        onClose={() => setShowEmailPopup(false)}
        onSuccess={handleJoinSuccess}
        referredBy={referredBy || referrals?.friend}
      />
    </div>
  );
};

export default MobileHeroLayout;